import {
    legacy_createStore as createStore,
    combineReducers,
    applyMiddleware,
} from 'redux';
import { useSelector, TypedUseSelectorHook } from 'react-redux';
import { thunk } from 'redux-thunk';

import { AppStore } from './type';
import { allows, allowsAny, Resource, Verb } from '../api/authz';
import counterStore from './counter';
import meStore from './me';
import authzStore from './authz';
import deviceStore from './device';
import materialStore from './material';

const reducers = combineReducers({
    counter: counterStore.reducer,
    me: meStore.reducer,
    authz: authzStore.reducer,
    device: deviceStore.reducer,
    material: materialStore.reducer,
});

const actions = {
    counter: counterStore.actions,
    me: meStore.actions,
    authz: authzStore.actions,
    device: deviceStore.actions,
    material: materialStore.actions,
};

const store = createStore(reducers, applyMiddleware(thunk));

if (import.meta.env.MODE !== 'production') {
    (window as any).RootStore = store.getState;
}

export const useAppSelector: TypedUseSelectorHook<AppStore> = useSelector;
export const useSiteRole = (): string => useAppSelector((s) => s.me.siteRole);
/**
 * 是否允许发起写操作 (2026-08-17 国服镜像): replica 站是只读镜像, 写入口一律隐藏
 * (后端 ReplicaReadonlyGuard 403 兜底, 仅留 admin 应急直写, 不在前端给入口);
 * master 站沿用"人人可提交走审批", 具体某个资源能不能改由 useCan 判定.
 *
 * 2026-09-01: 原实现是 `siteRole !== 'replica' || isAdmin`, 已去掉 isAdmin 分支 ——
 * 前端不再有"管理员"这个布尔身份, 权限一律走角色规则.
 */
export const useCanWrite = (): boolean =>
    useAppSelector((s) => s.me.siteRole !== 'replica');
export const useMe = () => useAppSelector((s) => s.me);
export const useAuthz = () => useAppSelector((s) => s.authz);
/**
 * 资源域级权限判定 (2026-08-31 RBAC). **入口可见性一律用本 hook**, 不要在页面里
 * 复制规则匹配逻辑 —— 前后端各存一份权限表必然漂移.
 *
 * 与后端中间件同一套规则本地判定, 以便渲染时就能看出
 * 菜单收敛效果; 未加载完 (loaded=false) 一律返回 false, 由调用方配合 loaded 做占位.
 *
 * 2026-09-01 起这是**唯一**的前端权限判定入口: useIsAdmin 已删除, 页面不要再按
 * "是不是管理员"控制入口, 一律声明"要哪个资源的哪个动作".
 */
export const useCan = (resource: Resource, verb: Verb): boolean =>
    useAppSelector((s) =>
        s.authz.loaded ? allows(s.authz.rules, resource, verb) : false,
    );
/** 对任一资源有该动作 (目前只用于 approve: 有任何资源的审批权就进审批中心全量视图). */
export const useCanAny = (verb: Verb): boolean =>
    useAppSelector((s) =>
        s.authz.loaded ? allowsAny(s.authz.rules, verb) : false,
    );
export const useDeviceCtx = () => useAppSelector((s) => s.device);
export const useMaterialCtx = () => useAppSelector((s) => s.material);

export default store;

export { actions };
